import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Terminal, ArrowRight } from "lucide-react";

const AUTH_BG = "https://static.prod-images.emergentagent.com/jobs/188eaab2-058f-442d-82ce-47107572346e/images/a0b62f12bffeac4ccfc0e64c260b28b5cdbc178f6ee06bbe9251f4ab2da90cf6.png";

export default function AuthPage({ mode = "login" }) {
    const { user, login, register, error, setError } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (user) return <Navigate to="/app" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const res = mode === "login" ? await login(email, password) : await register(email, password, name);
        setSubmitting(false);
        if (res.ok) nav("/app");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-black text-zinc-100">
            {/* Left: form */}
            <div className="flex flex-col justify-between p-8 lg:p-12 border-r border-zinc-800">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 bg-amber-500 flex items-center justify-center">
                        <Terminal className="w-5 h-5 text-black" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="font-heading font-black tracking-tight text-lg leading-none">AFFYNIX</div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-1">research terminal</div>
                    </div>
                </div>

                <div className="max-w-md w-full mx-auto py-12">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">
                        {mode === "login" ? "// Authenticate" : "// New Operator"}
                    </div>
                    <h1 className="font-heading font-black tracking-tighter text-4xl lg:text-5xl text-zinc-50 mb-3">
                        {mode === "login" ? "Welcome back." : "Establish access."}
                    </h1>
                    <p className="text-sm text-zinc-400 mb-8 leading-relaxed">
                        {mode === "login"
                            ? "Sign in to your private research workspace. Track firms, products, and revenue loops."
                            : "Create an operator account to start mapping firms, products, and revenue intelligence."}
                    </p>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="auth-form">
                        {mode === "register" && (
                            <div>
                                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Operator name</label>
                                <input
                                    data-testid="register-name-input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Jane Analyst"
                                    className="w-full bg-zinc-950 border border-zinc-800 px-3 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500 transition-colors"
                                />
                            </div>
                        )}
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Email</label>
                            <input
                                data-testid="auth-email-input"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@firm.com"
                                className="w-full bg-zinc-950 border border-zinc-800 px-3 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500 transition-colors font-mono"
                            />
                        </div>
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Password</label>
                            <input
                                data-testid="auth-password-input"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                className="w-full bg-zinc-950 border border-zinc-800 px-3 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500 transition-colors font-mono"
                            />
                        </div>

                        {error && (
                            <div data-testid="auth-error" className="border border-red-900/60 bg-red-950/30 text-red-400 text-xs px-3 py-2 font-mono">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            data-testid="auth-submit-btn"
                            disabled={submitting}
                            className="group w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold text-sm py-3 px-4 flex items-center justify-center gap-2 transition-colors"
                        >
                            {submitting ? "Processing…" : mode === "login" ? "SIGN IN" : "CREATE ACCOUNT"}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </button>
                    </form>

                    <div className="mt-6 text-sm text-zinc-500">
                        {mode === "login" ? (
                            <>No account? <Link onClick={() => setError("")} to="/signup" className="text-amber-500 hover:text-amber-400" data-testid="goto-signup">Create one →</Link></>
                        ) : (
                            <>Already an operator? <Link onClick={() => setError("")} to="/login" className="text-amber-500 hover:text-amber-400" data-testid="goto-login">Sign in →</Link></>
                        )}
                    </div>
                </div>

                <div className="font-mono text-[10px] text-zinc-600 tracking-widest">
                    SECURE • ENCRYPTED • OPERATOR-ONLY
                </div>
            </div>

            {/* Right: hero */}
            <div className="relative hidden lg:block overflow-hidden">
                <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
                <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-black/80" />
                <div className="relative h-full flex flex-col justify-end p-12">
                    <div className="border-l-2 border-amber-500 pl-5 max-w-md">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-3">// Intel Brief</div>
                        <div className="font-heading font-bold text-2xl text-zinc-50 leading-tight mb-2">
                            Map any firm. Decode its products. Track every revenue loop.
                        </div>
                        <div className="text-sm text-zinc-400">
                            Powered by Claude Sonnet 4.5 — generate dossiers in seconds, then refine with your own intel.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
